import BootstrapTooltip from "@/react/bootstrap/components/BootstrapTooltip";
import PageAddStoryPages from "@/react/story/components/adds/PageAddStoryPages";
import UiInput from "@/react/story/components/ui/UiInput";
import UiLoader from "@/react/story/components/ui/UiLoader";
import { IriEnums } from "@/react/story/enums/IriEnums";
import { useItems } from "@/react/story/providers/ItemsContext";
import { useStory } from "@/react/story/providers/StoryContext";
import type { JsonLdResponse, Story, StoryPage } from "@/react/story/types";
import ApiUtils from "@/react/story/utils/ApiUtils";
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

export default function PageAdd() {
    const Story = useStory();
    const Items = useItems();

    const [ story, setStory ] = useState<Story | null>(null);
    const [ filters, setFilters ] = useState<Partial<{
        page: number,
        name: string,
    }>>({
        page: 1,
        name: undefined,
    });

    const QueryStories = useQuery<JsonLdResponse<Story>>({
        queryKey: [ "add-story", filters ],
        queryFn: async () => (await ApiUtils.getCollection<Story>(IriEnums.STORY, { ...filters, deep: true })),
    });

    const handleCopyAllPages = async (pages: StoryPage[]) => {
        if (window.confirm(`Voulez-vous vraiment copier ces ${pages.length} page${pages.length > 1 ? "s" : ""} ?`)) {
            let idx = Items.getNewIdx();
            await Promise.all(pages.map(p => Items.createPage(p, idx++)));
        }
    };

    const handleLinkStory = async (story: Story) => {
        if (window.confirm("Voulez-vous vraiment ajouter cette story ?")) {
            void Items.createItem({
                owner: Story.story["@id"],
                story: story["@id"],
                idx: Items.getNewIdx()
            });
        }
    };

    const handleAddPage = async (page: StoryPage | undefined) => {
        if (window.confirm("Voulez-vous vraiment ajouter cette page ?")) {
            void Items.createPage(page, Items.getNewIdx());
        }
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        setFilters(p => ({ ...p, page: 1, name: formData.get("q")?.toString() ?? undefined }));
    };

    const stories = QueryStories?.data?.member ?? [];
    const count = QueryStories?.data?.totalItems ?? 0;
    const next = QueryStories?.data?.view?.next ?? undefined;
    const previous = QueryStories?.data?.view?.previous ?? undefined;

    return (
        <div className="d-flex flex-column gap-3">
            <h2 className="mb-0">Nouvelle page</h2>
            <div>
                <button type="button" onClick={() => handleAddPage(undefined)} className="btn btn-outline-success d-flex gap-1 border-success">
                    <span className="bi-plus"></span>
                    <span style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>Créer une nouvelle page vide</span>
                </button>
            </div>
            <hr />
            <div className="overflow-hidden">
                <div className="row">
                    <div className="col-6">
                        <div className="d-flex flex-column gap-3">
                            <h3 className="mb-0">Rechercher parmi vos stories ({count})</h3>
                            <div className="d-flex align-items-end gap-3">
                                <form onSubmit={handleSubmit}>
                                    <div className="input-group">
                                        <div className="input-group-text">
                                            {QueryStories.isFetching ? (
                                                <UiLoader />
                                            ) : (
                                                <span className="bi-search" />
                                            )}
                                        </div>
                                        <UiInput name="q" placeholder="Rechercher une story" />
                                    </div>
                                </form>
                                <BootstrapTooltip title="Page précédente">
                                    <button type="button" className="ms-auto btn btn-outline-secondary d-flex gap-1" onClick={() => setFilters(p => ({ ...p, page: Math.max(1, (p.page ?? 1) - 1) }))} disabled={!previous}>
                                        <span className="bi-arrow-left"></span>
                                    </button>
                                </BootstrapTooltip>
                                <BootstrapTooltip title="Page Suivante">
                                    <button type="button" className="btn btn-outline-secondary d-flex gap-1" onClick={() => setFilters(p => ({ ...p, page: (p.page ?? 1) + 1 }))} disabled={!next}>
                                        <span className="bi-arrow-right"></span>
                                    </button>
                                </BootstrapTooltip>
                            </div>
                            {0 < stories.length ? (
                                <div className="list-group">
                                    {stories.map(s => (
                                        <button
                                            key={s["@id"]}
                                            type="button"
                                            onClick={() => setStory(p => s.id === p?.id ? null : s)}
                                            className={`list-group-item list-group-item-action d-flex gap-3 ${s.id === story?.id ? "active" : ""}`}
                                        >
                                            <span className="me-auto">{s.name}</span>
                                            {Story.story.id === s.id ? (
                                                <BootstrapTooltip title="Story actuelle">
                                                    <span className="bi bi-star-fill text-warning"></span>
                                                </BootstrapTooltip>
                                            ) : null}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="alert alert-info" role="alert">
                                    <p className="mb-0">Aucune story ne correspond à votre recherche.</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-6">
                        {story ? (
                            <PageAddStoryPages
                                story={story}
                                onCopyAll={pages => handleCopyAllPages(pages)}
                                onAddStory={() => handleLinkStory(story)}
                                onCopy={page => handleAddPage(page)}
                                onCancel={() => setStory(null)}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
